import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';

/**
 * FormSection — visual grouping inside a Form.
 *
 * Renders an optional title + subtitle above the content (typically a
 * FormRow), with an optional divider above the section.
 *
 * Usage:
 *   <FormSection title="Address" subtitle="Mailing address for invoices">
 *     <FormRow>
 *       <FormField name="address1" label="Address" xs={12} />
 *       <FormField name="city"     label="City"    xs={12} sm={6} md={4} />
 *     </FormRow>
 *   </FormSection>
 *
 * Props:
 *   title       {string}  — section heading (optional)
 *   subtitle    {string}  — secondary description text (optional)
 *   divider     {boolean} — show a Divider above the section (default false)
 *   children    {node}    — FormRow(s) or any content
 *   sx          {object}  — extra sx on the outer Box
 */
export default function FormSection({ title, subtitle, divider = false, children, sx, ...rest }) {
  return (
    <Box sx={{ mb: 3, ...sx }} {...rest}>
      {divider && <Divider sx={{ mb: 2 }} />}

      {(title || subtitle) && (
        <Box sx={{ mb: 2 }}>
          {title && (
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {title}
            </Typography>
          )}
          {subtitle && (
            <Typography variant="body2" color="text.secondary">
              {subtitle}
            </Typography>
          )}
        </Box>
      )}

      {children}
    </Box>
  );
}
